import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import ActiveFilterSection from "../../../components/activeFilterSection";
import { useDashboardDataContext } from "../../../helpers/hooks/useDashboardDataContext";
import { useFilterNavigate } from "../../../helpers/hooks/useFilterNavigate";
import { getAnalyticsMetaWidgets } from "../../../helpers/utils";
import {
  clearFilterList,
  setAnalyticsFilters,
  setDisableWidgetSourcedFilters,
} from "../../../state/features/analytics/actions";
import { RootState } from "../../../state/store";
import AnalyticsWidgetsGrid from "./analyticsWidgetsGrid";
import { useAnalyticsFilters } from "../../../helpers/hooks/useAnalyticsFilters";
import LoadAnalyticsFilterData from "./loadAnalyticsFilterData";
import { ENTITY_LOCK_TYPE } from "../../../types/visibility";
import OverviewHeader from "../../../components/overviewHeader";
import WidgetsLoadingUi from "./widgetsLoadingUi";
import { useComparisonState } from "../../../features/comparison/hooks/useComparisonState";
import ComparisonView from "../../../features/comparison/containers/ComparisonView";

const AnalyticsLayout = () => {
  const [analyticsWidgets, setAnalyticsWidgets] = useState<any[]>([]);
  const [dashboardWidgetInfo, setDashboardWidgetInfo] = useState<any>(null);
  const { company } = useSelector((state: RootState) => state);
  const { company_details } = company;
  const isMaintenance = company_details?.maintenance_mode?.enabled;
  const [freezeDateRangeDropdown, setFreezeDateRangeDropdown] = useState(false);
  const [disableFiltersFromWidgets, setDisableFiltersFromWidgets] =
    useState(false);
  const [isComparisonEnabled, setIsComparisonEnabled] = useState(false);
  const {
    analyticsFiltersToFetch,
    setAnalyticsFilterUrls,
    shouldFetchFilterData,
  } = useAnalyticsFilters();
  const routeMatch = useParams();

  const dispatch = useDispatch();
  const layoutRef = useRef(null);
  const comparisonState = useComparisonState();

  const {
    dashboardWidgets,
    dashboardInfo,
    refreshDashboardData,
    shouldEnableDashboardFetch,
    isDashboardDataLoading,
  } = useDashboardDataContext();

  const navigateTo = useFilterNavigate();

  useEffect(() => {
    dispatch(clearFilterList());
    dispatch(setAnalyticsFilters([]));
    dispatch(setDisableWidgetSourcedFilters(false));
  }, [routeMatch.widget]);

  useEffect(() => {
    if (dashboardWidgets.length > 0 && dashboardInfo) {
      shouldEnableDashboardFetch(false);
      let dashboardWidget = dashboardWidgets.find(
        (d) => d.url === routeMatch.widget
      );
      if (dashboardWidget) {
        if ("is_timeless_widget" in dashboardWidget) {
          setFreezeDateRangeDropdown(dashboardWidget.is_timeless_widget);
        }
        const disableWidgetSourced = Boolean(
          dashboardWidget.disable_widget_sourced_filters
        );
        dispatch(setDisableWidgetSourcedFilters(disableWidgetSourced));
        if ("analytics_filters" in dashboardWidget) {
          setDisableFiltersFromWidgets(analyticsWidgets.length > 0);
          // Only the migrated (SubFilterConfiguration) shape — array of
          // `{ url, accessor, label }` — is consumed by the lazy dialog.
          // Legacy pages still emit string accessors and rely on the
          // widget-sourced `filterList`, so we keep redux `analyticsFilters`
          // empty for them.
          if (disableWidgetSourced) {
            dispatch(
              setAnalyticsFilters(dashboardWidget.analytics_filters || [])
            );
            setAnalyticsFilterUrls([]);
          } else {
            dispatch(setAnalyticsFilters([]));
            if (dashboardWidget.analytics_filters.length > 0) {
              setAnalyticsFilterUrls(dashboardWidget.analytics_filters);
            } else {
              setAnalyticsFilterUrls([]);
            }
          }
        }
        if ("is_comparison_enabled" in dashboardWidget) {
          setIsComparisonEnabled(dashboardWidget.is_comparison_enabled);
        }
        setDashboardWidgetInfo(dashboardWidget);
        if (dashboardWidget) {
          let metaWidgets = getAnalyticsMetaWidgets(dashboardWidget.widgets);
          setAnalyticsWidgets(metaWidgets);
        }
      } else {
        navigateTo(`/${dashboardInfo.api_path}`);
      }
    } else {
      refreshDashboardData();
    }
  }, [dashboardWidgets.length]);

  const handleShowReport = () => {
    navigateTo(`/${dashboardInfo.name}/${routeMatch.widget}/report`);
  };

  const spacerHeight = isMaintenance ? 160 : 150;

  return (
    <LayoutWrapper ref={layoutRef} isMaintenance={isMaintenance}>
      {isDashboardDataLoading ? (
        <WidgetsLoadingUi />
      ) : (
        <>
          <div className="spacer" style={{ height: `${spacerHeight}px` }}></div>
          {dashboardInfo && dashboardWidgetInfo ? (
            <>
              <OverviewHeader
                dashboardTitle={dashboardInfo?.display_name}
                analyticsDashboardTitle={
                  dashboardWidgetInfo.widget.title ?? ""
                }
                isComparisonEnabled={isComparisonEnabled}
                comparisonDimensions={
                  dashboardWidgetInfo?.comparison_dimensions
                }
                shouldFreezeDateRangeSelection={freezeDateRangeDropdown}
                isMaintenance={isMaintenance}
              />
              <ActiveFilterSection />
            </>
          ) : null}
          {comparisonState.isActive ? (
            <ComparisonView />
          ) : (
            <>
              {analyticsFiltersToFetch.length
                ? analyticsFiltersToFetch.map((url) => (
                    <LoadAnalyticsFilterData
                      key={url}
                      dashboard={routeMatch.dashboard || ""}
                      widget={routeMatch.widget || ""}
                      filterUrl={url}
                      shouldFetchFilterData={shouldFetchFilterData}
                    />
                  ))
                : null}
              {analyticsWidgets.length ? (
                <>
                  <div style={{ marginTop: isMaintenance ? "60px" : "0" }}>
                    <AnalyticsWidgetsGrid
                      analyticsWidgets={analyticsWidgets}
                      dashboard={routeMatch.dashboard || ""}
                      dashboardWidget={routeMatch.widget || ""}
                    />
                  </div>
                  <PageFooter>
                    {dashboardWidgetInfo &&
                    dashboardWidgetInfo.is_report_enabled ? (
                      <button
                        className="btn btn-primary"
                        onClick={handleShowReport}>
                        View Details
                      </button>
                    ) : null}
                  </PageFooter>
                </>
              ) : null}
            </>
          )}
        </>
      )}
    </LayoutWrapper>
  );
};

export default AnalyticsLayout;

const LayoutWrapper = styled.div<{ isMaintenance: boolean }>`
  height: calc(100vh - ${({ theme }) => theme.vars.navbar_height});
`;

const PageFooter = styled.div`
  margin: 13rem 58px;
  padding: 1rem 0;
  border-top: 1px solid #d2d2d2;
  text-align: right;
`;
